import { useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Share2, Copy, Check } from "lucide-react";
import { WhatsAppIcon, TelegramIcon, XIcon } from "@/components/BrandIcons";
import { toast } from "sonner";
import { fireGamify } from "@/lib/gamify";

// Easy sharing: native share sheet where available, else copy-link + WhatsApp / X / Telegram.
export default function ShareButton({ title, text, url, label, variant = "button", testid = "share-button" }) {
  const { pick } = useLang();
  const [copied, setCopied] = useState(false);
  // Share via the server-side preview route so link scrapers (WhatsApp/FB/LinkedIn)
  // get per-page title, description & image; humans are redirected to the real page.
  const pathname = typeof window !== "undefined" ? window.location.pathname : "/";
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const shareUrl = url || `${origin}/api/share${pathname}`;
  const shareText = text || title || "Dharmartha";

  const nativeShare = async () => {
    try {
      await navigator.share({ title: title || "Dharmartha", text: shareText, url: shareUrl });
      fireGamify("share");
    } catch { /* cancelled */ }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast(pick("लिंक कॉपी हो गया", "Link copied"));
      fireGamify("share");
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };

  const enc = encodeURIComponent;
  const links = [
    { key: "whatsapp", label: "WhatsApp", Icon: WhatsAppIcon, href: `https://wa.me/?text=${enc(`${shareText} ${shareUrl}`)}` },
    { key: "x", label: "X", Icon: XIcon, href: `https://twitter.com/intent/tweet?text=${enc(shareText)}&url=${enc(shareUrl)}` },
    { key: "telegram", label: "Telegram", Icon: TelegramIcon, href: `https://t.me/share/url?url=${enc(shareUrl)}&text=${enc(shareText)}` },
  ];

  const trigger =
    variant === "icon" ? (
      <button data-testid={testid} aria-label="Share" className="inline-flex h-9 w-9 items-center justify-center border border-line text-ink-2 transition-colors hover:border-saffron hover:text-saffron">
        <Share2 className="h-4 w-4" />
      </button>
    ) : (
      <button data-testid={testid} className="inline-flex items-center gap-2 border border-line px-4 py-2 text-sm text-ink-2 transition-colors hover:border-saffron hover:text-saffron">
        <Share2 className="h-4 w-4" /> {label || pick("साझा करें", "Share")}
      </button>
    );

  // On devices with a native share sheet, use it directly (no dropdown).
  const hasNative = typeof navigator !== "undefined" && !!navigator.share;
  if (hasNative) {
    return (
      <button data-testid={testid} onClick={nativeShare} className={variant === "icon"
        ? "inline-flex h-9 w-9 items-center justify-center border border-line text-ink-2 transition-colors hover:border-saffron hover:text-saffron"
        : "inline-flex items-center gap-2 border border-line px-4 py-2 text-sm text-ink-2 transition-colors hover:border-saffron hover:text-saffron"}>
        <Share2 className="h-4 w-4" /> {variant !== "icon" && (label || pick("साझा करें", "Share"))}
      </button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-none border-line bg-surface">
        <DropdownMenuItem data-testid="share-copy" onClick={copy} className="cursor-pointer gap-2 text-ink-2">
          {copied ? <Check className="h-4 w-4 text-saffron" /> : <Copy className="h-4 w-4" />} {pick("लिंक कॉपी करें", "Copy link")}
        </DropdownMenuItem>
        {links.map((l) => (
          <DropdownMenuItem key={l.key} asChild className="cursor-pointer gap-2 text-ink-2">
            <a href={l.href} target="_blank" rel="noreferrer" data-testid={`share-${l.key}`}><l.Icon className="h-4 w-4" /> {l.label}</a>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
